import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  ArrowBack,
  CalendarMonth,
  Public,
  Straighten,
  TextFields,
} from "@mui/icons-material";

import LanguagePicker from "../Translate/LanguagePicker";
import {
  useTranslate,
} from "../Translate/TranslateContext";
import {
  getSupportedLanguage,
  useRegional,
} from "../Regional/RegionalContext";
import { updateRegionalPreferences } from "../../api/regionalPreferences";
import { buttonPrimaryStyles } from "../../styles/ComponentStyles";

const DEFAULT_REGIONAL_PREFERENCES = {
  language: "en",
  timezone: "UTC",
  terminology: {
    field: "garden",
  },
  units: {
    temperature: "celsius",
    distance: "metric",
    area: "hectare",
    rainfall: "mm",
    wind: "kmh",
    yield: "t_ha",
    weight:"metric",
    waterdepth: "mm",
    watervolume: "l",
    radiation: "kwh_m2",
  },
  format: {
    date: "yyyy-MM-dd",
    time: "24h",
    firstDayOfWeek: "monday",
  },
};

const FIELD_TERMINOLOGY_OPTIONS = [
  { value: "garden", labelKey: "regional_field_garden" },
  { value: "field", labelKey: "regional_field_field" },
  { value: "yard", labelKey: "regional_field_yard" },
  { value: "plot", labelKey: "regional_field_plot" },
  { value: "block", labelKey: "regional_field_block" },
];

const TEMPERATURE_OPTIONS = [
  { value: "celsius", labelKey: "regional_temperature_celsius" },
  { value: "fahrenheit", labelKey: "regional_temperature_fahrenheit" },
];

const DISTANCE_OPTIONS = [
  { value: "metric", labelKey: "regional_distance_metric" },
  { value: "imperial", labelKey: "regional_distance_imperial" },
];

const WEIGHT_OPTIONS = [
  { value: "metric", labelKey: "regional_distance_metric" },
  { value: "imperial", labelKey: "regional_distance_imperial" },
];
const WATERDEPTH_OPTIONS = [
  { value: "mm", labelKey: "regional_waterdepth_mm" },
  { value: "in", labelKey: "regional_waterdepth_inches" },
];
const WATERVOLUME_OPTIONS = [
  { value: "l", labelKey: "regional_watervolume_liter" },
  { value: "gal", labelKey: "regional_watervolume_gallon" },
];
const RADIATION_OPTIONS = [
  { value: "mj_m2", labelKey: "regional_radiation_mj_m2" },
  { value: "kwh_m2", labelKey: "regional_radiation_kwh_m2" },
  { value: "btu_ft2", labelKey: "regional_radiation_btu_ft2" },
];
const AREA_OPTIONS = [
  { value: "hectare", labelKey: "regional_area_hectare" },
  { value: "acre", labelKey: "regional_area_acre" },
];

const RAINFALL_OPTIONS = [
  { value: "mm", labelKey: "regional_rainfall_mm" },
  { value: "in", labelKey: "regional_rainfall_in" },
];

const WIND_OPTIONS = [
  { value: "kmh", labelKey: "regional_wind_kmh" },
  { value: "mph", labelKey: "regional_wind_mph" },
];

const YIELD_OPTIONS = [
  { value: "t_ha", labelKey: "regional_yield_t_ha" },
  { value: "lb_acre", labelKey: "regional_yield_lb_acre" },
];

const DATE_FORMAT_OPTIONS = [
  { value: "yyyy-MM-dd", labelKey: "regional_date_iso" },
  { value: "MM/dd/yyyy", labelKey: "regional_date_us" },
  { value: "dd/MM/yyyy", labelKey: "regional_date_international" },
];

const TIME_FORMAT_OPTIONS = [
  { value: "24h", labelKey: "regional_time_24h" },
  { value: "12h", labelKey: "regional_time_12h" },
];

const FIRST_DAY_OPTIONS = [
  { value: "monday", labelKey: "regional_day_monday" },
  { value: "sunday", labelKey: "regional_day_sunday" },
];

const STATUS_MESSAGE_TIMEOUT_MS = 3500;

const CONTROL_BG = "var(--color-green-soft)";
const CONTROL_TEXT = "var(--color-green-strong)";
const CONTROL_BORDER = "var(--color-green-strong)";
const CONTROL_MENU_HOVER = "rgba(36, 94, 67, 0.1)";
const CONTROL_MENU_SELECTED = "rgba(36, 94, 67, 0.16)";
const CONTROL_MENU_SELECTED_HOVER = "rgba(36, 94, 67, 0.22)";

const selectPaperStyles = {
  bgcolor: CONTROL_BG,
  backgroundColor: CONTROL_BG,
  color: CONTROL_TEXT,
  border: `1px solid ${CONTROL_BORDER}`,
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.18)",
  "& .MuiMenu-list": {
    bgcolor: CONTROL_BG,
    backgroundColor: CONTROL_BG,
    color: CONTROL_TEXT,
    p: 0.5,
  },
  "& .MuiMenuItem-root": {
    bgcolor: CONTROL_BG,
    backgroundColor: CONTROL_BG,
    color: CONTROL_TEXT,
    borderRadius: 1,
    mx: 0.5,
    my: 0.25,
    "&:hover": {
      bgcolor: CONTROL_MENU_HOVER,
      backgroundColor: CONTROL_MENU_HOVER,
    },
    "&.Mui-selected": {
      bgcolor: CONTROL_MENU_SELECTED,
      backgroundColor: CONTROL_MENU_SELECTED,
      color: CONTROL_TEXT,
      fontWeight: 700,
      "&:hover": {
        bgcolor: CONTROL_MENU_SELECTED_HOVER,
        backgroundColor: CONTROL_MENU_SELECTED_HOVER,
      },
    },
  },
};

const selectInputStyles = {
  "& .MuiOutlinedInput-root": {
    bgcolor: CONTROL_BG,
    backgroundColor: CONTROL_BG,
    color: CONTROL_TEXT,
    borderRadius: 2,
    "& fieldset": {
      borderColor: CONTROL_BORDER,
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: CONTROL_BORDER,
      borderWidth: 2,
    },
    "&.Mui-focused fieldset": {
      borderColor: CONTROL_BORDER,
      borderWidth: 2,
    },
    "& .MuiSelect-select": {
      color: CONTROL_TEXT,
      WebkitTextFillColor: CONTROL_TEXT,
      fontWeight: 600,
    },
    "& .MuiAutocomplete-input": {
      color: CONTROL_TEXT,
      WebkitTextFillColor: CONTROL_TEXT,
      fontWeight: 600,
    },
    "& input": {
      color: CONTROL_TEXT,
      WebkitTextFillColor: CONTROL_TEXT,
      fontWeight: 600,
    },
    "& .MuiSvgIcon-root": {
      color: CONTROL_TEXT,
    },
  },
  "& .MuiInputLabel-root": {
    color: CONTROL_TEXT,
    opacity: 0.9,
    "&.Mui-focused": {
      color: CONTROL_TEXT,
      opacity: 1,
    },
    "&.MuiInputLabel-shrink": {
      color: CONTROL_TEXT,
      opacity: 1,
    },
  },
};

const languagePickerShellStyles = {
  ...selectInputStyles,
  "& .MuiFormControl-root": {
    width: "100%",
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: CONTROL_BG,
    backgroundColor: CONTROL_BG,
    color: CONTROL_TEXT,
    borderRadius: 2,
    "& fieldset": {
      borderColor: CONTROL_BORDER,
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: CONTROL_BORDER,
      borderWidth: 2,
    },
    "&.Mui-focused fieldset": {
      borderColor: CONTROL_BORDER,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-root": {
    bgcolor: CONTROL_BG,
    backgroundColor: CONTROL_BG,
    color: CONTROL_TEXT,
  },
  "& .MuiSelect-select": {
    color: CONTROL_TEXT,
    WebkitTextFillColor: CONTROL_TEXT,
    fontWeight: 600,
  },
  "& .MuiSvgIcon-root": {
    color: CONTROL_TEXT,
  },
  "& .MuiInputLabel-root": {
    color: CONTROL_TEXT,
    opacity: 0.9,
    "&.Mui-focused": {
      color: CONTROL_TEXT,
      opacity: 1,
    },
    "&.MuiInputLabel-shrink": {
      color: CONTROL_TEXT,
      opacity: 1,
    },
  },
};

const timezoneFilter = createFilterOptions({
  stringify: (timezone) => {
    const city = getTimezoneCity(timezone);
    const offset = getTimezoneOffset(timezone);

    return `${timezone} ${city} ${offset}`;
  },
});

function getTimezoneOptions() {
  const timezones = Intl.supportedValuesOf("timeZone");

  if (timezones.includes("UTC")) {
    return timezones;
  }

  return ["UTC", ...timezones];
}

function getTimezoneCity(timezone) {
  if (!timezone) return "";

  if (timezone === "UTC") {
    return "UTC";
  }

  return timezone.split("/").pop().replaceAll("_", " ");
}

function getTimezoneOffset(timezone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());

    return parts.find((part) => part.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
}

function getRegionalPreferencesFromUser(user) {
  const raw =
    user?.preferences ||
    user?.regional_preferences?.preferences ||
    user?.regionalPreferences?.preferences ||
    user?.regional_preferences ||
    user?.regionalPreferences;
  return raw?.preferences || raw || {};
}

function getRegionalPreferencesFromResponse(data, fallbackPreferences) {
  const responsePreferences =
    data?.preferences ||
    data?.user?.preferences ||
    data?.regional_preferences?.preferences ||
    data?.regionalPreferences?.preferences ||
    data?.user?.regional_preferences?.preferences ||
    data?.user?.regionalPreferences?.preferences ||
    data?.regional_preferences ||
    data?.regionalPreferences ||
    {};
  const raw = responsePreferences?.preferences || responsePreferences || {};

  return {
    ...fallbackPreferences,
    ...raw,
    terminology: {
      ...(fallbackPreferences?.terminology || {}),
      ...(raw?.terminology || {}),
    },
    units: {
      ...(fallbackPreferences?.units || {}),
      ...(raw?.units || {}),
    },
    format: {
      ...(fallbackPreferences?.format || {}),
      ...(raw?.format || {}),
    },
  };
}

function mergeRegionalPreferences(preferences) {
  return {
    ...DEFAULT_REGIONAL_PREFERENCES,
    ...(preferences || {}),
    language: getSupportedLanguage(
      preferences?.language || DEFAULT_REGIONAL_PREFERENCES.language
    ),
    terminology: {
      ...DEFAULT_REGIONAL_PREFERENCES.terminology,
      ...(preferences?.terminology || {}),
    },
    units: {
      ...DEFAULT_REGIONAL_PREFERENCES.units,
      ...(preferences?.units || {}),
    },
    format: {
      ...DEFAULT_REGIONAL_PREFERENCES.format,
      ...(preferences?.format || {}),
    },
  };
}

function setNestedValue(source, path, value) {
  const next = {
    ...source,
    terminology: { ...source.terminology },
    units: { ...source.units },
    format: { ...source.format },
  };

  if (path.length === 1) {
    next[path[0]] = value;
    return next;
  }

  next[path[0]][path[1]] = value;
  return next;
}

function Section({ icon, title, children }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}

        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {children}
    </Box>
  );
}

function SelectField({ label, value, options, onChange, translate }) {
  return (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      SelectProps={{
        MenuProps: {
          PaperProps: {
            sx: selectPaperStyles,
          },
        },
      }}
      sx={selectInputStyles}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {translate(option.labelKey)}
        </MenuItem>
      ))}
    </TextField>
  );
}

function TimezonePicker({ value, options, onChange, translate }) {
  return (
    <Autocomplete
      fullWidth
      size="small"
      options={options}
      value={value || "UTC"}
      filterOptions={timezoneFilter}
      getOptionLabel={(timezone) => timezone}
      isOptionEqualToValue={(option, selected) => option === selected}
      onChange={(event, timezone) => {
        if (timezone) {
          onChange(timezone);
        }
      }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: CONTROL_BG,
            backgroundColor: CONTROL_BG,
            color: CONTROL_TEXT,
            border: `1px solid ${CONTROL_BORDER}`,
          },
        },
      }}
      ListboxProps={{
        sx: {
          maxHeight: 300,
          bgcolor: CONTROL_BG,
          backgroundColor: CONTROL_BG,
          color: CONTROL_TEXT,
          p: 0.5,
          "& .MuiAutocomplete-option": {
            color: CONTROL_TEXT,
            bgcolor: CONTROL_BG,
            backgroundColor: CONTROL_BG,
            borderRadius: 1,
            "&[aria-selected='true']": {
              bgcolor: `${CONTROL_MENU_SELECTED} !important`,
              backgroundColor: `${CONTROL_MENU_SELECTED} !important`,
              fontWeight: 700,
            },
            "&.Mui-focused": {
              bgcolor: CONTROL_MENU_HOVER,
              backgroundColor: CONTROL_MENU_HOVER,
            },
          },
        },
      }}
      renderOption={(props, timezone) => {
        const city = getTimezoneCity(timezone);
        const offset = getTimezoneOffset(timezone);

        return (
          <Box
            component="li"
            {...props}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              py: 1,
              color: CONTROL_TEXT,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "inherit" }}
            >
              {city}
            </Typography>

            <Typography
              variant="caption"
              sx={{ opacity: 0.75, color: "inherit" }}
            >
              {timezone}
              {offset ? ` · ${offset}` : ""}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translate("timezone")}
          sx={selectInputStyles}
        />
      )}
    />
  );
}

export default function RegionalSettings({ user, onBack, setUser }) {
  const {
    translate,
    setLanguage,
  } = useTranslate();

  const { setRegionalPreferences } = useRegional();
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const userPreferences = useMemo(() => {
    return mergeRegionalPreferences(getRegionalPreferencesFromUser(user));
  }, [user]);

  const [preferences, setPreferences] = useState(userPreferences);
  const [savedPreferences, setSavedPreferences] = useState(userPreferences);
  const [saving, setSaving] = useState(false);
  const [messageKey, setMessageKey] = useState("");
  const [errorKey, setErrorKey] = useState("");

  useEffect(() => {
    setPreferences(userPreferences);
    setSavedPreferences(userPreferences);
  }, [userPreferences]);

  useEffect(() => {
    if (!messageKey && !errorKey) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setMessageKey("");
      setErrorKey("");
    }, STATUS_MESSAGE_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [messageKey, errorKey]);

  const hasChanges =
    JSON.stringify(preferences) !== JSON.stringify(savedPreferences);

  const statusKey = errorKey || messageKey;
  const statusSeverity = errorKey ? "error" : "success";

  const updateValue = (path, value) => {
    setMessageKey("");
    setErrorKey("");

    setPreferences((current) => setNestedValue(current, path, value));
  };

  const handleLanguageChange = (nextLanguage) => {
    updateValue(["language"], getSupportedLanguage(nextLanguage));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessageKey("");
    setErrorKey("");

    const preferencesToSave = mergeRegionalPreferences(preferences);

    try {
      const data = await updateRegionalPreferences({
        userId: user.user_id,
        preferences: preferencesToSave,
      });

      const nextPreferences = mergeRegionalPreferences(
        getRegionalPreferencesFromResponse(data, preferencesToSave)
      );

      setPreferences(nextPreferences);
      setSavedPreferences(nextPreferences);
      setRegionalPreferences(nextPreferences);
      setLanguage(nextPreferences.language);

      if (typeof setUser === "function") {
        setUser((currentUser) => {
          const baseUser = currentUser || user || {};

          return {
            ...baseUser,
            preferences: nextPreferences,
            regional_preferences: {
              ...(baseUser?.regional_preferences || {}),
              preferences: nextPreferences,
            },
            regionalPreferences: {
              ...(baseUser?.regionalPreferences || {}),
              preferences: nextPreferences,
            },
          };
        });
      }

      setMessageKey("regional_preferences_saved");
    } catch (error) {
      setErrorKey(error?.message || "regional_preferences_save_failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPreferences(savedPreferences);
    setMessageKey("");
    setErrorKey("");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBack fontSize="small" />
        </IconButton>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {translate("regional_preferences")}
        </Typography>
      </Box>

      <Section
        icon={<Public fontSize="small" />}
        title={translate("regional_language_timezone")}
      >
        <Box sx={languagePickerShellStyles}>
          <LanguagePicker
            value={preferences.language}
            onChange={handleLanguageChange}
            deferGlobalChange
          />
        </Box>

        <TimezonePicker
          value={preferences.timezone}
          options={timezoneOptions}
          onChange={(timezone) => updateValue(["timezone"], timezone)}
          translate={translate}
        />
      </Section>

      <Section
        icon={<TextFields fontSize="small" />}
        title={translate("regional_terminology")}
      >
        <SelectField
          label={translate("regional_field_terminology")}
          value={preferences.terminology.field}
          options={FIELD_TERMINOLOGY_OPTIONS}
          onChange={(value) => updateValue(["terminology", "field"], value)}
          translate={translate}
        />
      </Section>

      <Section
        icon={<Straighten fontSize="small" />}
        title={translate("regional_units")}
      >
        <SelectField
          label={translate("regional_temperature_unit")}
          value={preferences.units.temperature}
          options={TEMPERATURE_OPTIONS}
          onChange={(value) => updateValue(["units", "temperature"], value)}
          translate={translate}
        />
             <SelectField
          label={translate("regional_radiation_unit")}
          value={preferences.units.radiation}
          options={RADIATION_OPTIONS}
          onChange={(value) => updateValue(["units", "radiation"], value)}
          translate={translate}
        />
        <SelectField
          label={translate("regional_weight_unit")}
          value={preferences.units.weight}
          options={WEIGHT_OPTIONS}
          onChange={(value) => updateValue(["units", "weight"], value)}
          translate={translate}
        />
        <SelectField
          label={translate("regional_distance_unit")}
          value={preferences.units.distance}
          options={DISTANCE_OPTIONS}
          onChange={(value) => updateValue(["units", "distance"], value)}
          translate={translate}
        />
           <SelectField
          label={translate("regional_waterdepth_unit")}
          value={preferences.units.waterdepth}
          options={WATERDEPTH_OPTIONS}
          onChange={(value) => updateValue(["units", "waterdepth"], value)}
          translate={translate}
        />
   <SelectField
          label={translate("regional_watervolume_unit")}
          value={preferences.units.watervolume}
          options={WATERVOLUME_OPTIONS}
          onChange={(value) => updateValue(["units", "watervolume"], value)}
          translate={translate}
        />
      
        <SelectField
          label={translate("regional_area_unit")}
          value={preferences.units.area}
          options={AREA_OPTIONS}
          onChange={(value) => updateValue(["units", "area"], value)}
          translate={translate}
        />

        <SelectField
          label={translate("regional_rainfall_unit")}
          value={preferences.units.rainfall}
          options={RAINFALL_OPTIONS}
          onChange={(value) => updateValue(["units", "rainfall"], value)}
          translate={translate}
        />

        <SelectField
          label={translate("regional_wind_unit")}
          value={preferences.units.wind}
          options={WIND_OPTIONS}
          onChange={(value) => updateValue(["units", "wind"], value)}
          translate={translate}
        />

        <SelectField
          label={translate("regional_yield_unit")}
          value={preferences.units.yield}
          options={YIELD_OPTIONS}
          onChange={(value) => updateValue(["units", "yield"], value)}
          translate={translate}
        />
      </Section>

      <Section
        icon={<CalendarMonth fontSize="small" />}
        title={translate("regional_format")}
      >
        <SelectField
          label={translate("regional_date_format")}
          value={preferences.format.date}
          options={DATE_FORMAT_OPTIONS}
          onChange={(value) => updateValue(["format", "date"], value)}
          translate={translate}
        />

        <SelectField
          label={translate("regional_time_format")}
          value={preferences.format.time}
          options={TIME_FORMAT_OPTIONS}
          onChange={(value) => updateValue(["format", "time"], value)}
          translate={translate}
        />

        <SelectField
          label={translate("regional_first_day_of_week")}
          value={preferences.format.firstDayOfWeek}
          options={FIRST_DAY_OPTIONS}
          onChange={(value) => updateValue(["format", "firstDayOfWeek"], value)}
          translate={translate}
        />
      </Section>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          disabled={!hasChanges || saving}
          onClick={handleReset}
          sx={{
            flex: "1 1 160px",
            borderRadius: 2,
            fontWeight: 600,
            borderColor: CONTROL_BORDER,
            color: CONTROL_TEXT,
            "&:hover": {
              borderColor: CONTROL_BORDER,
              bgcolor: CONTROL_MENU_HOVER,
            },
          }}
        >
          {translate("reset")}
        </Button>

        <Button
          variant="contained"
          disabled={!hasChanges || saving}
          onClick={handleSave}
          sx={{
            ...buttonPrimaryStyles,
            flex: "1 1 180px",
          }}
        >
          {saving ? translate("saving") : translate("save_changes")}
        </Button>

        {statusKey && (
          <Alert
            severity={statusSeverity}
            sx={{
              flex: "0 1 auto",
              width: "fit-content",
              maxWidth: "100%",
              minHeight: 36,
              py: 0,
              px: 1.25,
              borderRadius: 2,
              fontSize: "0.82rem",
              alignItems: "center",
              "& .MuiAlert-icon": {
                mr: 0.75,
              },
              "& .MuiAlert-message": {
                py: 0.35,
              },
            }}
          >
            {errorKey ? statusKey : translate(statusKey)}
          </Alert>
        )}
      </Box>
    </Box>
  );
}